"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowUpRight,
  Building2,
  Factory,
  Handshake,
  HeartHandshake,
  Newspaper,
  Target,
} from "lucide-react";
import { AnthropicPartnerBadge } from "@/components/ui/anthropic-partner-badge";
import { getLocalizedPageContent } from "@/content/localized-pages";
import { t } from "@/content/i18n/messages";
import {
  defaultLocale,
  getLocalizedPathname,
  type Locale,
  type TranslatedLocale,
} from "@/lib/i18n";

const officeImage = "/images/company/office-dark.jpg";

interface CompanyProps {
  locale?: Locale;
}

function isTranslatedLocale(locale: Locale): locale is TranslatedLocale {
  return locale !== defaultLocale;
}

const englishAboutContent = [
  "Anchor Sprint is built by technologists, strategists, and designers dedicated to unlocking Malaysian enterprises' potential. We combine local insight with global-grade delivery to help organisations adopt AI and automation with confidence.",
  "From our Kuala Lumpur headquarters, we partner with leaders to modernise operations, empower teams, and deliver the measurable outcomes stakeholders expect.",
].join("\n\n");

const englishValuesContent = [
  "Our values centre on trust, transparency, and tangible outcomes. We champion ethical AI adoption, inclusive collaboration, and long-term partnerships that respect every stakeholder.",
  "Whether we are architecting a new data platform or guiding change management, our teams lead with empathy and a shared commitment to doing what is right for our clients and their communities.",
].join("\n\n");

export default function Company({ locale = defaultLocale }: CompanyProps) {
  const prefersReducedMotion = useReducedMotion();
  const translate = (text: string) => t(locale, text);

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const localizedContent = isTranslatedLocale(locale)
    ? getLocalizedPageContent("company", locale)
    : null;

  const aboutParagraphs = (
    localizedContent?.sections[0]?.body ?? englishAboutContent
  ).split("\n\n");
  const valuesParagraphs = (
    localizedContent?.sections[1]?.body ?? englishValuesContent
  ).split("\n\n");

  const companyLinks: Array<{
    title: string;
    description: string;
    path: string;
    icon: LucideIcon;
  }> = [
    {
      title: "Our People",
      description: "Meet our co-founders, Jazz Tong and Liew Jee Fong",
      path: "/our-people/",
      icon: Building2,
    },
    {
      title: "About Us",
      description: "Learn about our company history, vision, and leadership",
      path: "/about-us/",
      icon: Building2,
    },
    {
      title: "Corporate Values",
      description: "Discover the core values that drive our business",
      path: "/corporate-values/",
      icon: HeartHandshake,
    },
    {
      title: "Corporate Mission",
      description:
        "Understand what drives us and our commitment to excellence",
      path: "/corporate-mission/",
      icon: Target,
    },
    {
      title: "Industries We Serve",
      description:
        "Explore the diverse industries we serve across Malaysia",
      path: "/industries-we-serve/",
      icon: Factory,
    },
    {
      title: "Our Partners",
      description:
        "Meet our strategic partners who help us deliver exceptional results",
      path: "/our-partners/",
      icon: Handshake,
    },
    {
      title: "Blog & Insights",
      description:
        "Read our latest thinking on AI, automation, and digital transformation",
      path: "/blog/",
      icon: Newspaper,
    },
  ];

  const partnerLogos: Array<{ name: string; src: string }> = [
    { name: "Lenovo", src: "/images/partners/lenovo.png" },
    { name: "Microsoft", src: "/images/partners/microsoft.png" },
    { name: "AWS", src: "/images/partners/aws.png" },
    { name: "Adobe", src: "/images/partners/adobe.png" },
  ];

  return (
    <div className="bg-background">
      {/* Unified page header */}
      <section className="bg-background pb-16 pt-24 md:pb-20 md:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...reveal(0)}
            className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
          >
            {translate("Company")}
          </motion.p>
          <motion.h1
            {...reveal(0.08)}
            className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl"
          >
            {translate("One studio.")}{" "}
            <span className="emphasis-underline">
              {translate("Every discipline.")}
            </span>
          </motion.h1>
          <motion.p
            {...reveal(0.16)}
            className="mt-5 max-w-2xl text-lg text-neutral-muted"
          >
            {translate(
              "Get to know the team, values, mission, and partners behind Anchor Sprint's work across Malaysia and ASEAN.",
            )}
          </motion.p>
        </div>
      </section>
      <div className="h-px w-full bg-border" aria-hidden />

      {/* Company hub links */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Explore the company")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Find your way around")}{" "}
              <span className="emphasis-underline">
                {translate("the studio")}
              </span>
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-neutral-muted">
              {translate(
                "Six quick paths into who we are, what we believe, and who we work with.",
              )}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {companyLinks.map(
              ({ title, description, path, icon: Icon }, index) => (
                <motion.div
                  key={path}
                  initial={prefersReducedMotion ? false : { y: 12 }}
                  whileInView={{ y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.6,
                    delay: (index % 3) * 0.08,
                    ease: [0.21, 0.6, 0.35, 1],
                  }}
                  className="h-full"
                >
                  <Link
                    href={getLocalizedPathname(path, locale)}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
                  >
                    <div className="mb-6 flex items-start justify-between">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" aria-hidden />
                      </div>
                      <span className="font-mono text-sm text-neutral-muted/60">
                        0{index + 1}
                      </span>
                    </div>
                    <h2 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                      {translate(title)}
                    </h2>
                    <p className="mb-6 flex-1 text-sm leading-relaxed text-neutral-muted">
                      {translate(description)}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 group-hover:gap-2.5">
                      {translate("Explore")}
                      <ArrowUpRight className="h-4 w-4" aria-hidden />
                    </span>
                  </Link>
                </motion.div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* About Anchor Sprint — image + text */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: [0.21, 0.6, 0.35, 1] }}
              className="overflow-hidden rounded-2xl"
            >
              <img
                src={officeImage}
                alt={translate("Anchor Sprint office at night, lit by screens and city lights")}
                className="h-[320px] w-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.6,
                delay: 0.1,
                ease: [0.21, 0.6, 0.35, 1],
              }}
            >
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {translate("The studio")}
              </p>
              <h2 className="mb-6 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
                {translate("About Anchor Sprint")}
              </h2>
              <div className="space-y-4">
                {aboutParagraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-base leading-relaxed text-neutral-muted"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              <Link
                href={getLocalizedPathname("/about-us/", locale)}
                className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 hover:gap-2.5"
              >
                {translate("Explore About Us")}
                <ArrowUpRight className="h-4 w-4" aria-hidden />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Corporate values — hairline panel */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.21, 0.6, 0.35, 1] }}
            className="rounded-2xl border border-border bg-card px-6 py-12 md:px-12"
          >
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("What we believe")}
            </p>
            <h2 className="mb-6 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Corporate Values")}
            </h2>
            <div className="max-w-3xl space-y-4">
              {valuesParagraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-base leading-relaxed text-neutral-muted"
                >
                  {paragraph}
                </p>
              ))}
            </div>
            <Link
              href={getLocalizedPathname("/corporate-values/", locale)}
              className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 hover:gap-2.5"
            >
              {translate("View Our Values")}
              <ArrowUpRight className="h-4 w-4" aria-hidden />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Partner & certification logo strip — proof element */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Partners & certifications")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("The company")}{" "}
              <span className="emphasis-underline">
                {translate("we keep")}
              </span>
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-neutral-muted">
              {translate(
                "The technology partnerships behind every Anchor Sprint engagement.",
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, ease: [0.21, 0.6, 0.35, 1] }}
              className="col-span-2 flex h-24 items-center justify-center rounded-2xl border border-border bg-card px-4 sm:col-span-1 lg:col-span-2"
            >
              <AnthropicPartnerBadge variant="hero" />
            </motion.div>
            {partnerLogos.map(({ name, src }, index) => (
              <motion.div
                key={name}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.5,
                  delay: (index + 1) * 0.06,
                  ease: [0.21, 0.6, 0.35, 1],
                }}
                className="flex h-24 items-center justify-center rounded-2xl border border-border bg-card px-6"
              >
                <img
                  src={src}
                  alt={name}
                  loading="lazy"
                  className="max-h-9 w-full object-contain opacity-80"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-5xl">
            {translate("Ready to work")}{" "}
            <span className="emphasis-underline">
              {translate("with us?")}
            </span>
          </h2>
          <p className="mt-5 text-lg text-neutral-muted">
            {translate(
              "Let's discuss how we can help transform your business with our comprehensive technology solutions and expertise.",
            )}
          </p>
          <div className="mt-9">
            <Link
              href={getLocalizedPathname("/contact/", locale)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {translate("Meet with Us")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
